import { Controller, type UseFormReturn } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import { useAllocatedDownpayment } from '../api/use-allocated-downpayment';
import { useBlOrderPaymentSchedule } from '../api/use-bl-order-payment-schedule';
import { BankAccountField } from './bank-account-field';
import { contractTermsFromSchedule } from './final-invoice-sell-contract-terms';
import { FinalDownpaymentBanner, FinalSettlementSummary } from './final-invoice-sell-summary';
import type { FinalSettlementSubmitValues } from './final-settlement-invoice-schema';
import type { WatchedTotals } from './final-settlement-form';
import { PaymentScheduleEditor } from './payment-schedule-editor';
import { isoDateValue, type BlContextForTradeInvoice } from './trade-invoice-payload';

interface FinalInvoiceSellFormProps {
  readonly form: UseFormReturn<FinalSettlementSubmitValues>;
  readonly totals: WatchedTotals;
  readonly blOrderId: string;
  readonly currency: string | null;
  readonly counterpartyName: string | null;
  readonly bl: BlContextForTradeInvoice;
  readonly disabled: boolean;
  // On edit the schedule is neither re-hydrated nor saved, so the editor is
  // replaced by a read-only note rather than a phantom that discards changes.
  readonly scheduleLocked: boolean;
}

// The sell-side (receivable) Final Invoice: a stripped, schedule-driven layout
// matching Launchpad. Identity fields (number, currency, status, company) are
// auto-derived in the defaults and hidden here; the operator only sets Quantity,
// Due Date, Remarks, and the payment schedule.
export function FinalInvoiceSellForm(props: FinalInvoiceSellFormProps) {
  const allocatedQuery = useAllocatedDownpayment(props.blOrderId);
  const scheduleQuery = useBlOrderPaymentSchedule(props.blOrderId);
  const terms = contractTermsFromSchedule(scheduleQuery.data?.receivable ?? null);
  const gross = props.totals.marketReferencePrice * props.totals.amountQtMt;
  const amountDue = Math.max(props.totals.net, 0);
  const hasPriorPayments = props.totals.allocated + props.totals.provisional > 0;
  return (
    <div className="flex flex-col gap-4">
      <SellHeaderContext bl={props.bl} customer={props.counterpartyName} />
      <SellCoreFields {...props} />
      {hasPriorPayments && (
        <FinalDownpaymentBanner
          allocated={props.totals.allocated}
          remaining={amountDue}
          currency={props.currency}
        />
      )}
      {props.scheduleLocked ? (
        <LockedScheduleNote />
      ) : (
        <Controller
          control={props.form.control}
          name="payment_schedule"
          render={({ field }) => (
            <PaymentScheduleEditor
              totalAmount={amountDue}
              currency={props.currency}
              contractTermsLabel={terms.label}
              triggerLine={terms.triggerLine}
              tranches={field.value ?? []}
              onTranchesChange={field.onChange}
              disabled={props.disabled}
            />
          )}
        />
      )}
      <FinalSettlementSummary
        gross={gross}
        allocated={props.totals.allocated}
        provisional={props.totals.provisional}
        net={props.totals.net}
        currency={props.currency}
        blShipmentCount={allocatedQuery.data?.bl_shipment_count ?? null}
        totalShipments={allocatedQuery.data?.total_shipments ?? null}
      />
    </div>
  );
}

// Shown in place of the schedule editor when editing an existing invoice: the
// schedule was fixed into payment rows at creation and can't be changed here.
function LockedScheduleNote() {
  return (
    <div
      className="text-muted-foreground border-t pt-3 text-sm"
      data-testid="payment-schedule-locked-note"
    >
      <p className="font-medium">Payment schedule</p>
      <p>
        The payment schedule is set when the invoice is first created and can&apos;t be changed
        here. To use a different schedule, delete this invoice and create it again.
      </p>
    </div>
  );
}

function SellCoreFields(props: FinalInvoiceSellFormProps) {
  return (
    <div className="flex flex-col gap-3 border-t pt-2">
      <QuantityField form={props.form} bl={props.bl} disabled={props.disabled} />
      <UnitPriceField form={props.form} currency={props.currency} disabled={props.disabled} />
      <DueDateField form={props.form} disabled={props.disabled} />
      <BankAccountField
        registration={props.form.register('payment_account_key')}
        disabled={props.disabled}
      />
      <RemarksField form={props.form} disabled={props.disabled} />
    </div>
  );
}

function SellHeaderContext(props: {
  readonly bl: BlContextForTradeInvoice;
  readonly customer: string | null;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 text-sm" data-testid="final-invoice-header">
      <HeaderField label="Contract Number" value={props.bl.contractNumber} />
      <HeaderField label="BL Order Number" value={props.bl.blOrderName} />
      <HeaderField label="BL Number" value={props.bl.blNumber} />
      <HeaderField label="Customer" value={props.customer} />
    </div>
  );
}

function HeaderField(props: { readonly label: string; readonly value: string | null }) {
  return (
    <div>
      <span className="text-muted-foreground text-xs">{props.label}</span>
      <p className="font-medium">{props.value ?? '—'}</p>
    </div>
  );
}

function QuantityField(props: {
  readonly form: UseFormReturn<FinalSettlementSubmitValues>;
  readonly bl: BlContextForTradeInvoice;
  readonly disabled: boolean;
}) {
  const shipped = props.bl.shippedQuantityMt === null ? 0 : Number(props.bl.shippedQuantityMt);
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="sell-final-quantity">Quantity (MT)</Label>
      <Input
        id="sell-final-quantity"
        type="number"
        step="0.001"
        disabled={props.disabled}
        {...props.form.register('amount_qt_mt')}
      />
      <p className="text-muted-foreground text-xs">
        Shipped quantity: {shipped.toLocaleString()} MT
      </p>
    </div>
  );
}

// Editable so the operator can correct the price when the agreed value differs
// from the ticket-derived default (which seeds the field on open). Kept in the
// form so the watched totals recompute live as it changes.
function UnitPriceField(props: {
  readonly form: UseFormReturn<FinalSettlementSubmitValues>;
  readonly currency: string | null;
  readonly disabled: boolean;
}) {
  const unit = props.currency === null ? 'Unit Price' : `Unit Price (${props.currency}/MT)`;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="sell-final-unit-price">{unit}</Label>
      <Input
        id="sell-final-unit-price"
        type="number"
        step="0.01"
        min="0"
        inputMode="decimal"
        disabled={props.disabled}
        data-testid="final-invoice-unit-price"
        {...props.form.register('market_reference_price')}
      />
      <p className="text-muted-foreground text-xs">
        Defaults to the signed contract price — edit if the agreed price differs.
      </p>
    </div>
  );
}

function dateInputValue(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return isoDateValue(value);
  if (typeof value === 'string') return value.slice(0, 10);
  return '';
}

function DueDateField(props: {
  readonly form: UseFormReturn<FinalSettlementSubmitValues>;
  readonly disabled: boolean;
}) {
  return (
    <Controller
      control={props.form.control}
      name="original_due_date"
      render={({ field }) => (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sell-final-due-date">Due Date</Label>
          <Input
            id="sell-final-due-date"
            type="date"
            disabled={props.disabled}
            value={dateInputValue(field.value)}
            onChange={(e) => {
              field.onChange(e.target.value);
            }}
          />
        </div>
      )}
    />
  );
}

function RemarksField(props: {
  readonly form: UseFormReturn<FinalSettlementSubmitValues>;
  readonly disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="sell-final-remarks">Remarks (Optional)</Label>
      <Textarea
        id="sell-final-remarks"
        rows={2}
        placeholder="Additional notes..."
        disabled={props.disabled}
        {...props.form.register('remarks')}
      />
    </div>
  );
}
